import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  StyleSheet,
  Text,
  TextInput,
  View,
} from './components/base_components';
import { BusyOverlay, useBusy } from './components/busy_component';
import Breadcrumb from './components/breadcrumb';
import TitleActionLine from './components/title_action_line';
import StandardBoxList from './components/standard_box_list';
import VolumeTabBar from './components/volume_tab_bar';
import TextButton from './components/buttons/text_button';

import MediaGrantAdminStore from './stores/media_grant_admin_store';
import UserStore from './stores/user_store';
import VolumeStore from './stores/volume_store';

const styles = StyleSheet.create({
  volumePermissionScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  tabBar: {
    marginLeft: '2rem',
    marginRight: '2rem',
  },
  dialog: {
    marginLeft: '2rem',
    marginBottom: '2rem',
    flexDirection: 'column',
  },
  empty: {
    margin: '2em',
    fontSize: '1.5em',
  },
  text: {
    marginTop: '1em',
    fontSize: '1.5em',
  },
  textInput: {
    alignSelf: 'stretch',
    maxWidth: '40rem',
    marginTop: '1rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '4rem',
    color: '#555',
    fontSize: '1.5rem',
    borderRadius: '0.5rem',
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: '#f5f5f5',
  },
  button: {
    marginTop: '1rem',
    alignSelf: 'flex-start',
  },
  addBox: {
    marginTop: 0,
  },
});

export default function VolumePermissionScreen(props) {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org = UserStore.useOrg();
  const volume_id = parseInt(params.volume_id ?? '0', 10) || 0;
  const volume = VolumeStore.useVolume(org?.org_id, volume_id);
  const grant_list = MediaGrantAdminStore.useVolumeList(org?.org_id, volume_id);
  const [grant_name, setGrantName] = React.useState('');
  const [prefix_path, setPrefixPath] = React.useState('');
  const navigate = useNavigate();

  React.useEffect(() => {
    MediaGrantAdminStore.fetch(org?.org_id);
    VolumeStore.fetch(org?.org_id);
  }, [org?.org_id]);

  function _onAddGrantPress() {
    if (grant_name && prefix_path && setBusy()) {
      const opts = { org_id: org?.org_id, volume_id, grant_name, prefix_path };
      if (!opts.prefix_path.endsWith('/')) {
        opts.prefix_path = opts.prefix_path + '/';
      }
      MediaGrantAdminStore.add(opts, (err) => {
        clearBusy();
        if (err === 'used_grant_name' || err === 409) {
          window.alert('Name already in use.');
        } else if (err === 'used_prefix_path') {
          window.alert('Grant already exists with the path.');
        } else if (err) {
          window.alert('Failed to add grant, please try again.');
        } else {
          navigate(`/volume/${volume_id}/grant/${grant_name}`);
          setGrantName('');
          setPrefixPath('');
        }
      });
    }
  }

  const grant_rows = grant_list?.map?.((grant) => ({
    url: `/volume/${volume_id}/grant/${grant.grant_name}`,
    list: [
      grant.grant_name,
      grant.prefix_path,
      grant.is_disabled ? 'Disabled' : 'Enabled',
      _userList(grant),
      _deviceList(grant),
    ],
  }));

  return (
    <View style={styles.volumePermissionScreen}>
      <Breadcrumb
        list={[
          { text: 'Volumes', url: '/volume' },
          { text: volume?.volume_name, url: `/volume/${volume_id}` },
          'Permissions',
        ]}
      />
      <TitleActionLine title={volume?.volume_name} />
      <VolumeTabBar style={styles.tabBar} volumeId={volume_id} />
      <StandardBoxList
        title='Grants'
        detail={grant_rows?.length ?? 0}
        headers={['Container Name', 'Path', 'Status', 'Users', 'Devices']}
        sizes={[2, 2, 1, 3, 3]}
        rows={grant_rows}
      >
        {grant_rows?.length === 0 ? (
          <Text style={styles.empty}>No grants found.</Text>
        ) : null}
      </StandardBoxList>
      <StandardBoxList style={styles.addBox} title='Add Grant'>
        <View style={styles.dialog}>
          <Text style={styles.text}>
            Add a permission grant to this volume.
          </Text>
          <TextInput
            style={styles.textInput}
            placeholder='Contaner Name'
            value={grant_name}
            onChangeText={setGrantName}
          />
          <TextInput
            style={styles.textInput}
            placeholder='Prefix Path'
            value={prefix_path}
            onChangeText={setPrefixPath}
          />
          <TextButton
            style={styles.button}
            text='Add Grant'
            onPress={_onAddGrantPress}
          />
        </View>
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
function _deviceList(grant) {
  let ret = grant.device_list
    .slice(0, 5)
    .map((u) => u.device_name)
    .join(', ');
  if (grant.device_list.length > 5) {
    ret += ' ...';
  }
  return ret;
}
function _userList(grant) {
  let ret = grant.user_list
    .slice(0, 5)
    .map((u) => u.user_name || u.email)
    .join(', ');
  if (grant.user_list.length > 5) {
    ret += ' ...';
  }
  return ret;
}
